import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bot, Check, CheckCheck, CircleAlert, Clock, FileText, Loader2, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { classNames, formatDateTime } from '@/lib/utils';
import type { MensajeWhatsApp } from '@/lib/comunicaciones';

/**
 * Las piezas de un chat de WhatsApp en el CRM, para Conversaciones y para la
 * ficha de un lead: los mensajes como burbujas y el cuadro para contestar.
 *
 * Colores fijos y en `style`: el modo oscuro del CRM oscurece `.bg-white` en
 * toda la app, y un chat de WhatsApp se ve igual siempre.
 */

/**
 * En el móvil no hay Mayús+Intro: el Intro del teclado tiene que ser un salto
 * de línea, y se envía con el botón.
 */
const TACTIL = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

/** Quien escribió desde el CRM. Producción a veces no lo sabe y guarda «unknown»: eso no se enseña. */
const autor = (por?: string) => (por && !['unknown', 'desconocido'].includes(por) ? por.split('@')[0] : null);

export function MensajesWhatsApp({ mensajes, vacio, className, anchoBurbuja = 'max-w-[80%]' }: {
  mensajes: MensajeWhatsApp[];
  vacio?: ReactNode;
  className?: string;
  /** En una bandeja ancha, más estrechas que en el panel del lead. */
  anchoBurbuja?: string;
}) {
  const fin = useRef<HTMLDivElement>(null);
  useEffect(() => { fin.current?.scrollIntoView({ block: 'end' }); }, [mensajes.length]);

  return (
    <div className={classNames('overflow-auto px-4 py-4 space-y-2', className)} style={{ background: '#EFE7DE' }}>
      {mensajes.length === 0 && vacio}
      {mensajes.map(m => (
        <div key={m.id} className={classNames('flex', m.direccion === 'saliente' ? 'justify-end' : 'justify-start')}>
          <div className={classNames(anchoBurbuja, 'rounded-lg px-3 py-1.5 shadow-sm text-[14px]')} style={{ color: '#111B21', background: m.direccion === 'saliente' ? '#D9FDD3' : '#FFFFFF' }}>
            {m.plantilla && (
              <div className="text-[11px] flex items-center gap-1 mb-0.5" style={{ color: '#667781' }} title="Las conversaciones se abren con una plantilla aprobada por Meta">
                <FileText size={11} /> plantilla «{m.plantilla}»
              </div>
            )}
            <div className="whitespace-pre-wrap break-words">{m.texto}</div>
            <div className="flex items-center justify-end gap-1 text-[11px] mt-0.5" style={{ color: '#667781' }}>
              {m.automatico && <span className="flex items-center gap-0.5" title="Respuesta automática"><Bot size={11} /> automática ·</span>}
              {autor(m.por) && <span>{autor(m.por)} ·</span>}
              {formatDateTime(m.en)}
              {m.direccion === 'saliente' && (
                m.estado === 'failed' ? <CircleAlert size={12} className="text-red-500" aria-label={m.detalle || 'No se entregó'} />
                  : m.estado === 'read' ? <CheckCheck size={12} className="text-sky-500" />
                  : m.estado === 'delivered' ? <CheckCheck size={12} />
                  : <Check size={12} />
              )}
            </div>
            {m.estado === 'failed' && m.detalle && <div className="text-[11px] text-red-600">{m.detalle}</div>}
          </div>
        </div>
      ))}
      <div ref={fin} />
    </div>
  );
}

/**
 * Texto libre: solo dentro de las 24 h siguientes al último mensaje de la
 * persona (`hasta`). Quien lo usa decide qué enseñar fuera de esa ventana.
 */
export function RedactarWhatsApp({ telefono, hasta, onEnviado }: { telefono: string; hasta: string | null; onEnviado: () => void | Promise<void> }) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setTexto(''); setError(null); }, [telefono]);

  const enviar = async () => {
    if (!texto.trim()) return;
    setEnviando(true);
    setError(null);
    try {
      await api.responderWhatsApp(telefono, texto.trim());
      setTexto('');
      await onEnviado();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo enviar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-2">
      {error && <div className="text-xs text-red-600">{error}</div>}
      <div className="flex gap-2">
        <textarea
          className="input flex-1 text-sm resize-none"
          rows={2}
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !TACTIL) { e.preventDefault(); void enviar(); } }}
          placeholder={TACTIL ? 'Escribe la respuesta…' : 'Escribe la respuesta… (Intro para enviar, Mayús+Intro para salto de línea)'}
        />
        <button type="button" onClick={() => void enviar()} disabled={enviando || !texto.trim()} className="btn-primary px-3 self-end" aria-label="Enviar">
          {enviando ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
      {hasta && (
        <div className="text-[11px] text-sereno-dark/40 flex items-center gap-1">
          <Clock size={11} /> Puedes escribirle libremente hasta el {formatDateTime(hasta)}.
        </div>
      )}
    </div>
  );
}
